import React, { useMemo, useState } from "react";
import styled from "styled-components";
import getConfig from "next/config";
import { Row, Col } from "react-bootstrap";

import { FORM_FIELD_KEYS } from "../../../../constants/forms";
import { getFieldProps } from "../../../../utils/form-fields";

import TextInput from "../../../controls/TextInput";
import Select from "../../../controls/Select";
import Checkbox from "../../../controls/Checkbox";

const { publicRuntimeConfig } = getConfig();

const getYearOptions = () => {
  const startYear = new Date().getFullYear();

  return Array.from(new Array(20), (_, i) => {
    return { value: (startYear + i) % 100, title: `${startYear + i}` };
  });
}

const StyledRow = styled(Row)`
  .col-md-6 {
    padding-right: 8px;
  }

  .col-md-6 + .col-md-6 {
    padding-left: 8px;
    padding-right: 15px;
  }

  .col-md-8 {
    padding-right: 8px;
  }

  .col-md-4 {
    padding-right: 8px;
  }

  .col-md-4 + .col-md-4 {
    padding-left: 7px;
    padding-right: 7px;
  }

  .col-md-4 + .col-md-4 + .col-md-4 {
    padding-right: 15px;
  }

  @media ${({ theme }) => theme.devices.tablet} {
    .col-12 {
      position: relative;

      &:nth-child(6) > div {
        padding-top: 0;
      }
    }

    .col-md-6 {
      padding-right: 15px;
    }

    .col-md-6 + .col-md-6 {
      padding-left: 15px;
    }

    .col-md-8 {
      padding-right: 15px;

      .form-group {
        padding-top: 32px;
      }
    }

    .col-md-4 {
      padding-right: 15px;
    }

    .col-md-4 + .col-md-4 {
      padding-left: 15px;
      padding-right: 15px;
    }

    .col-md-4 + .col-md-4 + .col-md-4 {
      padding-right: 15px;
    }

  }
`;

function CreditCardInfoBlock({
  isCardExpired,
  paymentInputs,
  values = {},
  onChange
}) {
  const [selectionTarget, setSelectionTarget] = useState(null);

  const { meta, getCardNumberProps, getCVCProps } = paymentInputs || {};
  const { erroredInputs, touchedInputs } = meta;

  const showBillingAddress = values[FORM_FIELD_KEYS.billingAddress] === "true";

  const isCardNumberInvalid = useMemo(() => {
    return values.cardNumber !== "0000000000000000" && (
      (touchedInputs.cardNumber && erroredInputs.cardNumber) || 
      (!values.cardNumber && values.cardNumber.length === 0 && !values.beforeSubmit)
    );
  }, [values, touchedInputs.cardNumber, erroredInputs.cardNumber]);

  const isCVCInvalid = useMemo(() => {
    return (touchedInputs.cvc && erroredInputs.cvc) || (!values.cvc && values.cvc.length === 0 && !values.beforeSubmit);
  }, [values, touchedInputs.cvc, erroredInputs.cvc]);

  function getPaymentExtraProps(name) {
    if (name === selectionTarget) {
      console.log({ name })
      return { onKeyPress: () => {} };
    }

    return {};
  }

  function handleSelect(event) {
    let elem = event.target;
    let selectionLength = elem.selectionEnd - elem.selectionStart;
    if (!selectionLength) {
      setSelectionTarget(null);
    } else {
      setSelectionTarget(elem.name);
    }
  }

  return (
    <StyledRow>
      <Col xs={12}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.firstName)}
          value={values[FORM_FIELD_KEYS.firstName]}
          isInvalid={!values[FORM_FIELD_KEYS.firstName] && !values.beforeSubmit}
          onChange={onChange}
        />
      </Col>
      <Col xs={12}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.lastName)}
          value={values[FORM_FIELD_KEYS.lastName]}
          isInvalid={!values[FORM_FIELD_KEYS.lastName] && !values.beforeSubmit}
          onChange={onChange}
        />
      </Col>
      <Col xs={12} md={8}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.cardNumber)}
          value={values[FORM_FIELD_KEYS.cardNumber]}
          thirdPartyProps={getCardNumberProps({ onChange })}
          {...getPaymentExtraProps(FORM_FIELD_KEYS.cardNumber)}
          onSelect={handleSelect}
          isInvalid={isCardNumberInvalid}
          defaultInvalidMessage={erroredInputs.cardNumber}
          data-threeds="pan"
        />
        <div className="card-providers mobile">
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/visa-alternative.png`} 
            alt="VISA" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/mastercard-alternative.png`} 
            alt="Mastercard" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/discover-alternative.png`} 
            alt="Discover" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/american-express-alternative.png`} 
            alt="American Express" 
          />
        </div>
      </Col>
      <Col xs={12} md={4}>
        <div className="card-providers desktop">
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/visa-alternative.png`} 
            alt="VISA" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/mastercard-alternative.png`} 
            alt="Mastercard" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/discover-alternative.png`} 
            alt="Discover" 
          />
          <img
            src={`${publicRuntimeConfig.basePath}/images/V2/american-express-alternative.png`} 
            alt="American Express" 
          />
        </div>
      </Col>
      <Col xs={12} md={6}>
        <Select
          {...getFieldProps(FORM_FIELD_KEYS.cardMonth)}
          value={values[FORM_FIELD_KEYS.cardMonth]}
          isInvalid={isCardExpired && !values.beforeSubmit}
          onChange={onChange}
          data-threeds="month"
        />
      </Col>
      <Col xs={12} md={6}>
        <Select
          {...getFieldProps(FORM_FIELD_KEYS.cardYear)}
          options={getYearOptions()}
          value={values[FORM_FIELD_KEYS.cardYear]}
          isInvalid={isCardExpired && !values.beforeSubmit}
          onChange={onChange}
          data-threeds="year"
        />
      </Col>
      <Col xs={12}>
        <TextInput
          {...getFieldProps(FORM_FIELD_KEYS.cvc)}
          value={values[FORM_FIELD_KEYS.cvc]}
          thirdPartyProps={getCVCProps({ onChange })}
          isInvalid={isCVCInvalid}
          defaultInvalidMessage={erroredInputs.cvc}
        />
      </Col>
      <Col xs={12}>
        <Checkbox
          {...getFieldProps(FORM_FIELD_KEYS.billingAddress)}
          value={values[FORM_FIELD_KEYS.billingAddress]}
          onChange={onChange}
        />
      </Col>
      {showBillingAddress && (
        <>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.billingAddressAddress1)}
              pattern="^[ a-zA-Z0-9\\/-]+$"
              defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
              value={values[FORM_FIELD_KEYS.billingAddressAddress1]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={6}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.billingAddressAddress2)}
              pattern="^[ a-zA-Z0-9\\/-]+$"
              defaultInvalidMessage="The only special characters allowed in the Street field are - and /"
              value={values[FORM_FIELD_KEYS.billingAddressAddress2]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={4}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.billingAddressCity)}
              value={values[FORM_FIELD_KEYS.billingAddressCity]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={4}>
            <TextInput
              {...getFieldProps(FORM_FIELD_KEYS.billingAddressZip)}
              value={values[FORM_FIELD_KEYS.billingAddressZip]}
              onChange={onChange}
            />
          </Col>
          <Col xs={12} md={4}>
            <Select
              {...getFieldProps(FORM_FIELD_KEYS.billingAddressState)}
              value={values[FORM_FIELD_KEYS.billingAddressState]}
              validated={values.validated}
              onChange={onChange}
            />
          </Col>
        </>
      )}
      <Col xs={12}>
        <div className="inputs-block-divider" />
      </Col>
    </StyledRow>
  );
}

export default CreditCardInfoBlock;
